import type { AnalyticsDailyRow, AnalyticsJobBucket, AnalyticsLocationRow, SkipCategory } from "../../api/contracts";
import type { ChartSeries } from "../components/charts";

export type Days = 7 | 30 | 90;
export const DAYS_OPTIONS: readonly Days[] = [7, 30, 90];

export function parseDays(raw: string | null): Days {
  const n = Number(raw);
  return (DAYS_OPTIONS as readonly number[]).includes(n) ? (n as Days) : 30;
}

/** Whole-percent change against the previous range; null when the previous range had nothing. */
export function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

/** Nine job buckets become five series: the four that matter for load, and everything else. */
export const JOB_GROUPS: { label: string; buckets: AnalyticsJobBucket[] }[] = [
  { label: "Webhook re-plans", buckets: ["replan_location_day"] },
  { label: "Evaluates", buckets: ["evaluate"] },
  { label: "Nightly re-plans", buckets: ["nightly_replan"] },
  { label: "Backfills", buckets: ["backfill"] },
  { label: "Other", buckets: ["reconcile_managed_blocks", "directory_sync", "preview", "smoke_test", "other"] },
];

export function jobSeries(rows: AnalyticsDailyRow[]): ChartSeries[] {
  return JOB_GROUPS.map((g) => ({ label: g.label, values: rows.map((r) => g.buckets.reduce((a, b) => a + r.jobs[b], 0)) }));
}

export function blockLabels(mode: "shadow" | "live"): { created: string; removed: string; resized: string } {
  return mode === "live" ? { created: "Placed", removed: "Removed", resized: "Resized" } : { created: "Would place", removed: "Would remove", resized: "Would resize" };
}

export const SKIP_LABELS: Record<SkipCategory, string> = {
  gate_disabled: "Location not enabled", gate_paused: "Location or system paused", gate_unmanaged: "Block not managed by this system",
  protected: "Protected hours", coverage: "Someone kept in the salon", conflict: "Appointment or block in the way", past: "Shift edge already passed", locked: "Start time locked for the next day", duplicate: "Duplicate BTB block", other: "Other",
};

export const tierLabel = (i: number): string => `Tier ${i + 1}`;

export type LocationSortKey = "name" | "events" | "jobs" | "failedJobs" | "created" | "removed" | "writeErrors" | "placedMinutes" | "activeBlocks" | "blockedMinutes";

export function sortLocations(rows: AnalyticsLocationRow[], key: LocationSortKey, dir: "asc" | "desc"): AnalyticsLocationRow[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const cmp = key === "name" ? a.name.localeCompare(b.name) : a[key] - b[key];
    return cmp !== 0 ? cmp * sign : a.name.localeCompare(b.name);
  });
}
